import React, { useState } from 'react';
import EditTransactionModal from './EditTransactionModal';
import './TransactionDetailModal.css';

function TransactionDetailModal({ transaction, onClose, onDelete, onTransactionUpdated }) {
  const [showEditModal, setShowEditModal] = useState(false);
  
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF',
      'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$', 'CNY': '¥', 'INR': '₹'
    };
    return symbols[currency] || currency || '£';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Determine transaction type
  const isIncome = transaction.type === 'income';
  const isHouseholdExpense = transaction.type === 'household_expense';
  const isExpense = transaction.type === 'expense' || isHouseholdExpense;
  
  const symbol = getCurrencySymbol(transaction.currency);

  // Calculate display amount based on transaction type
  const getDisplayAmount = () => {
    if (isIncome) {
      return parseFloat(transaction.net_amount || transaction.amount || 0);
    } else if (isHouseholdExpense) {
      return parseFloat(transaction.user_split_amount || transaction.amount || 0);
    } else {
      return parseFloat(transaction.amount || 0);
    }
  };

  const displayAmount = getDisplayAmount();

  // Get description based on transaction type
  const getDescription = () => {
    return transaction.description || transaction.notes || '';
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleEditSave = () => {
    setShowEditModal(false);
    if (onTransactionUpdated) {
      onTransactionUpdated();
    }
    onClose();
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(transaction);
    }
  };

  // If edit modal is open, render it instead
  if (showEditModal) {
    return (
      <EditTransactionModal
        transaction={transaction}
        onClose={() => setShowEditModal(false)}
        onSave={handleEditSave}
      />
    );
  }

  return (
    <div className="detail-modal-overlay" onClick={onClose}>
      <div className="detail-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="detail-modal-header">
          <div className={`detail-type-badge ${isIncome ? 'income' : 'expense'}`}>
            {isIncome ? '💰 Income' : isHouseholdExpense ? '👥 Household Expense' : '💸 Expense'}
          </div>
          <button className="detail-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="detail-modal-body">
          {/* Amount */}
          <div className="detail-amount-section">
            <div className="detail-amount-label">Amount</div>
            <div className={`detail-amount ${isIncome ? 'income' : 'expense'}`}>
              {isIncome ? '+' : '-'}{symbol}{Math.abs(displayAmount).toFixed(2)}
            </div>
          </div>

          {/* Description */}
          <div className="detail-row">
            <div className="detail-label">Description</div>
            <div className="detail-value">{getDescription() || 'No description'}</div>
          </div>

          {/* Date */}
          <div className="detail-row">
            <div className="detail-label">Date</div>
            <div className="detail-value">{formatDate(transaction.date)}</div>
          </div>

          {/* Source (for income) */}
          {isIncome && transaction.source && (
            <div className="detail-row">
              <div className="detail-label">Source</div>
              <div className="detail-value">{transaction.source}</div>
            </div>
          )}

          {/* Category (for expenses) */}
          {isExpense && (
            <>
              <div className="detail-row">
                <div className="detail-label">Category</div>
                <div className="detail-value">{transaction.category || 'N/A'}</div>
              </div>
              {transaction.subcategory && (
                <div className="detail-row">
                  <div className="detail-label">Subcategory</div>
                  <div className="detail-value">{transaction.subcategory}</div>
                </div>
              )}
            </>
          )}

          {/* Household expense specific fields */}
          {isHouseholdExpense && (
            <>
              <div className="detail-divider"></div>
              <div className="detail-section-title">Split Details</div>
              
              <div className="detail-row">
                <div className="detail-label">Total Amount</div>
                <div className="detail-value">{symbol}{parseFloat(transaction.amount || 0).toFixed(2)}</div>
              </div>
              
              <div className="detail-row">
                <div className="detail-label">Your Share</div>
                <div className="detail-value">{transaction.split_percentage || 50}%</div>
              </div>
              
              <div className="detail-row">
                <div className="detail-label">Your Amount</div>
                <div className="detail-value highlight">{symbol}{parseFloat(transaction.user_split_amount || 0).toFixed(2)}</div>
              </div>
              
              {transaction.household_name && (
                <div className="detail-row">
                  <div className="detail-label">Household</div>
                  <div className="detail-value">{transaction.household_name}</div>
                </div>
              )}
            </>
          )}

          {/* Income specific fields */}
          {isIncome && (
            <>
              <div className="detail-divider"></div>
              <div className="detail-section-title">Breakdown</div>
              
              <div className="detail-row">
                <div className="detail-label">Gross Amount</div>
                <div className="detail-value">{symbol}{parseFloat(transaction.gross_amount || transaction.amount || 0).toFixed(2)}</div>
              </div>
              
              {parseFloat(transaction.income_tax || 0) > 0 && (
                <div className="detail-row">
                  <div className="detail-label">Income Tax</div>
                  <div className="detail-value deduction">-{symbol}{parseFloat(transaction.income_tax).toFixed(2)}</div>
                </div>
              )}
              
              {parseFloat(transaction.social_contributions || 0) > 0 && (
                <div className="detail-row">
                  <div className="detail-label">Social Contributions</div>
                  <div className="detail-value deduction">-{symbol}{parseFloat(transaction.social_contributions).toFixed(2)}</div>
                </div>
              )}
              
              {parseFloat(transaction.student_loan || 0) > 0 && (
                <div className="detail-row">
                  <div className="detail-label">Student Loan</div>
                  <div className="detail-value deduction">-{symbol}{parseFloat(transaction.student_loan).toFixed(2)}</div>
                </div>
              )}
              
              <div className="detail-row net-row">
                <div className="detail-label">Net Amount</div>
                <div className="detail-value net-value">{symbol}{parseFloat(transaction.net_amount || 0).toFixed(2)}</div>
              </div>
            </>
          )}

          {/* Notes */}
          {transaction.notes && !isHouseholdExpense && (
            <>
              <div className="detail-divider"></div>
              <div className="detail-section-title">Notes</div>
              <div className="detail-notes">{transaction.notes}</div>
            </>
          )}

          {/* Metadata */}
          <div className="detail-divider"></div>
          <div className="detail-metadata">
            <div className="metadata-item">
              <span className="metadata-label">Currency:</span>
              <span className="metadata-value">{transaction.currency || 'GBP'}</span>
            </div>
            {transaction.created_at && (
              <div className="metadata-item">
                <span className="metadata-label">Created:</span>
                <span className="metadata-value">{formatDateTime(transaction.created_at)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="detail-modal-footer">
          <button className="detail-btn delete-btn" onClick={handleDeleteClick}>
            🗑️ Delete
          </button>
          <button className="detail-btn edit-btn" onClick={handleEditClick}>
            ✏️ Edit
          </button>
        </div>
      </div>
    </div>
  );
}

export default TransactionDetailModal;
